import { useMutation, useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  getIncentivesByPlatformIdPersonByPersonIdUnacknowledged,
  getPlatform,
  getUser,
  postIncentivesByPlatformIdReferralByReferralRewardIdAcknowledgeByPersonIdByAwardedReferralRewardId,
} from "api/methods";
import { Text } from "@/atoms";
import { Button } from "@/atoms/button/Button";
import RepeatCash from "@/atoms/icon/icons/RepeatCash.svg";
import {
  AnimatedBackground,
  AnimatedBackgroundConfigType,
} from "@/components/AnimatedBackground/AnimatedBackground";
import { PlatformLogo } from "@/components/PlatformLogo";
import { useTranslation } from "@/core/i18n";
import { useCountUp } from "@/hooks";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { useUser } from "@/modules/user/useUser/useUser";
import { queryClient } from "@/pages/Root";

type ReferralRewardsAcknowledgeRewardsModalProps = {
  readonly platformId: string;
  readonly onCloseAfterInteracting: () => void;
};

type AcknowledgeReferralRewardVariables = {
  referralRewardId: string;
  awardedReferralRewardId: string;
  type: "regular" | "bonus";
};

type UnacknowledgedRewardsResponse = NonNullable<
  Awaited<ReturnType<typeof getIncentivesByPlatformIdPersonByPersonIdUnacknowledged>>
>;

type PendingReward =
  | (UnacknowledgedRewardsResponse["awardedReferralRewards"][number] & { type: "regular" })
  | (UnacknowledgedRewardsResponse["bonusAwardedReferralRewards"][number] & { type: "bonus" });

const getPendingRewards = (
  response: UnacknowledgedRewardsResponse | undefined,
): PendingReward[] => {
  const regular =
    response?.awardedReferralRewards?.map((reward) => ({
      ...reward,
      type: "regular" as const,
    })) ?? [];

  const bonus =
    response?.bonusAwardedReferralRewards?.map((reward) => ({
      ...reward,
      type: "bonus" as const,
    })) ?? [];

  return [...regular, ...bonus];
};

const removeAcknowledgedRewardFromCache = (
  current: UnacknowledgedRewardsResponse,
  awardedReferralRewardId: string,
  type: "regular" | "bonus",
): UnacknowledgedRewardsResponse => {
  if (type === "bonus") {
    return {
      ...current,
      bonusAwardedReferralRewards: current.bonusAwardedReferralRewards?.filter(
        (reward) => reward.awardedReferralRewardId.value !== awardedReferralRewardId,
      ),
    };
  }

  return {
    ...current,
    awardedReferralRewards: current.awardedReferralRewards?.filter(
      (reward) => reward.awardedReferralRewardId.value !== awardedReferralRewardId,
    ),
  };
};

export const ReferralRewardsAcknowledgeRewardsModal = ({
  platformId,
  onCloseAfterInteracting,
}: ReferralRewardsAcknowledgeRewardsModalProps) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [acknowledgedInSession, setAcknowledgedInSession] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const { user } = useUser();

  const { data: platform } = useQuery({ ...getPlatform.keyed() });

  const unacknowledgedRewardsQuery = getIncentivesByPlatformIdPersonByPersonIdUnacknowledged.keyed({
    platformId,
    personId: user?.id ?? "",
  });

  const { data: unacknowledgedRewardsResponse, isPending: isLoading } = useQuery({
    ...unacknowledgedRewardsQuery,

    enabled: !!platformId && !!user?.id,
  });

  const pendingRewards = useMemo(() => {
    return getPendingRewards(unacknowledgedRewardsResponse).filter(
      (reward) => !acknowledgedInSession.has(reward.awardedReferralRewardId.value),
    );
  }, [acknowledgedInSession, unacknowledgedRewardsResponse]);

  const currentReward = pendingRewards[0];

  useEffect(() => {
    if (pendingRewards.length > 0 && !isLoading) {
      setShowModal(true);
      document.body.classList.add("overflow-hidden");
      return;
    }

    if (pendingRewards.length === 0 && showModal) {
      setShowModal(false);
      document.body.classList.remove("overflow-hidden");

      if (acknowledgedInSession.size > 0) {
        onCloseAfterInteracting();
        setAcknowledgedInSession(new Set());
      }
    }
  }, [
    acknowledgedInSession.size,
    isLoading,
    onCloseAfterInteracting,
    pendingRewards.length,
    showModal,
  ]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  const acknowledgeMutation = useMutation({
    mutationFn: async ({
      referralRewardId,
      awardedReferralRewardId,
    }: AcknowledgeReferralRewardVariables) => {
      if (!user?.id) {
        throw new Error("User must be signed in to acknowledge referral rewards.");
      }

      await postIncentivesByPlatformIdReferralByReferralRewardIdAcknowledgeByPersonIdByAwardedReferralRewardId(
        {
          platformId,
          referralRewardId,
          personId: user.id,
          awardedReferralRewardId,
        },
      );
    },
    onSuccess: (_data, { awardedReferralRewardId, type }) => {
      if (!user?.id) {
        return;
      }

      setAcknowledgedInSession((current) => {
        const next = new Set(current);
        next.add(awardedReferralRewardId);
        return next;
      });

      queryClient.setQueryData<UnacknowledgedRewardsResponse>(
        unacknowledgedRewardsQuery.queryKey,
        (current) => {
          if (!current) {
            return current;
          }

          return removeAcknowledgedRewardFromCache(current, awardedReferralRewardId, type);
        },
      );

      queryClient.invalidateQueries(getUser.keyed());
      queryClient.invalidateQueries({
        ...unacknowledgedRewardsQuery,
        refetchType: "none",
      });
    },
  });

  const handleAcknowledge = () => {
    if (!currentReward) {
      return;
    }

    acknowledgeMutation.mutate({
      referralRewardId: currentReward.referralRewardAtTheTime.id,
      awardedReferralRewardId: currentReward.awardedReferralRewardId.value,
      type: currentReward.type,
    });
  };

  const getRepeatCashValue = () => {
    if (!currentReward) return 0;

    if (currentReward.type === "bonus") {
      const bonusValue = currentReward.referralRewardAtTheTime.bonusRepeatCashAmountInCents || 0;
      return bonusValue / 100;
    }

    const value = currentReward.referralRewardAtTheTime.repeatCashAmountInCents || 0;
    return value / 100;
  };

  const value = useCountUp({
    to: getRepeatCashValue(),
    from: 0,
    duration: 1500,
    delay: 250,
  });

  const getRewardTitle = () => {
    if (!currentReward) return "";

    if (currentReward.type === "bonus") {
      return t("referralRewardsAcknowledgeRewardsModal.bonusRewardTitle");
    }

    if (
      currentReward.type === "regular" &&
      "referredPersonName" in currentReward &&
      currentReward.referredPersonName
    ) {
      return t("referralRewardsAcknowledgeRewardsModal.thanksForReferring", {
        referredPersonName: getPersonFirstNameLastInitial(currentReward.referredPersonName),
      });
    }

    return t("referralRewardsAcknowledgeRewardsModal.newRewardTitle");
  };

  const getRewardDescription = () => {
    if (!currentReward) return "";

    if (currentReward.type === "bonus") {
      return currentReward.referralRewardAtTheTime.bonusRewardTitle?.text?.trim() ?? "";
    }

    return currentReward.referralRewardAtTheTime.rewardTitle?.text?.trim() ?? "";
  };

  const renderHeader = () => (
    <div className="flex items-center justify-end px-4 py-2">
      <button
        onClick={handleAcknowledge}
        className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
        aria-label={t("referralRewardsAcknowledgeRewardsModal.closeModalAriaLabel")}
      >
        <X size={24} />
      </button>
    </div>
  );

  const renderBonusContent = () => {
    if (!currentReward || currentReward.type !== "bonus") return null;

    const referredNames =
      "referredPersonNames" in currentReward ? currentReward.referredPersonNames : [];

    return (
      <div className="flex flex-1 flex-col items-center justify-between">
        <motion.div
          key={currentReward.awardedAtUtc.toString()}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="flex w-full flex-col items-center gap-4 text-center"
        >
          <PlatformLogo
            logoUrl={platform?.logoImageUrl}
            platformName={platform?.name}
            className="h-12 w-auto max-w-[180px]"
          />

          <Text
            text={t("referralRewardsAcknowledgeRewardsModal.extraSpecialThankYou")}
            variant="h1"
            className="whitespace-pre-line font-serif text-2xl font-medium italic leading-snug text-gray-800"
          />

          <div className="flex flex-col items-center gap-3">
            <Text
              text={t("referralRewardsAcknowledgeRewardsModal.thanksForReferringList")}
              variant="body1"
              className="text-gray-600"
            />
            <div className="flex flex-wrap justify-center gap-2">
              {referredNames?.map((name, index) => (
                <div
                  key={`${name}-${index}`}
                  className="rounded-full bg-white/80 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm"
                >
                  {getPersonFirstNameLastInitial(name)}
                </div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="relative mt-4 w-full"
            style={{ aspectRatio: "1.586" }}
          >
            <div className="relative h-full w-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#D4876F] via-[#ECA696] to-[#C98B6B] px-8 py-16 shadow-2xl">
              <AnimatedBackground
                durationSeconds={10}
                className="absolute inset-0 h-full w-full"
                backgroundConfig={{
                  baseColor: "#D4876F",
                  color1: "#ECA696",
                  color2: "#ffffff",
                  tint1Percent: 40,
                  tint2Percent: 50,
                  spot1SizePercent: 70,
                  spot2SizePercent: 65,
                  vignetteOpacity: 0.2,
                }}
              />

              <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-amber-900/20" />
              <div className="absolute inset-0 bg-gradient-to-tl from-white/30 via-transparent to-transparent" />

              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                  backgroundSize: "32px 32px",
                }}
              />

              <div className="absolute right-8 top-6 text-4xl font-light text-amber-950/30">+</div>
              <div className="absolute bottom-6 left-8 text-5xl font-light text-amber-950/40">
                +
              </div>
              <div className="absolute left-1/4 top-10 text-3xl font-light text-amber-950/25">
                +
              </div>
              <div className="absolute bottom-10 right-1/4 text-3xl font-light text-amber-950/25">
                +
              </div>
              <div className="absolute right-16 top-1/2 text-2xl font-light text-amber-950/20">
                +
              </div>
              <div className="absolute bottom-1/3 left-16 text-2xl font-light text-amber-950/20">
                +
              </div>

              <div className="relative flex h-full items-center justify-center">
                <div className="flex items-center gap-3 font-sans text-3xl font-bold drop-shadow-sm">
                  {currentReward.referralRewardAtTheTime.referralRewardType === "RepeatCash" ? (
                    <>
                      <RepeatCash className="h-20 w-20 text-black" />
                      <Text
                        text={`$${value}`}
                        variant="v3-h1"
                        className="text-6xl font-bold not-italic text-black"
                      />
                    </>
                  ) : (
                    <span className="font-bold not-italic text-black">
                      {currentReward.referralRewardAtTheTime.bonusRewardTitle?.text}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  };

  const renderRegularContent = () => {
    if (currentReward?.type !== "regular") return null;

    const rewardType = currentReward.referralRewardAtTheTime.referralRewardType;

    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <motion.div
          key={currentReward.awardedAtUtc.toString()}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
          className="flex w-full flex-col items-center gap-4 text-center"
        >
          <PlatformLogo
            logoUrl={platform?.logoImageUrl}
            platformName={platform?.name}
            className="h-16 w-auto max-w-[200px]"
          />

          <Text text={getRewardTitle()} variant="v3-h2" />

          <div className="flex flex-col items-center gap-3">
            <Text
              text={t("referralRewardsAcknowledgeRewardsModal.receivedRewardForReferral")}
              variant="body1"
              className="text-gray-600"
            />
          </div>

          <motion.div
            className="flex items-center gap-4 rounded-full bg-white/20 px-6 py-4 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            {rewardType === "RepeatCash" && (
              <>
                <RepeatCash className="h-16 w-16" />
                <Text text={`$${value}`} variant="v3-h2" />
              </>
            )}
            {rewardType !== "RepeatCash" && <Text text={getRewardDescription()} variant="v3-h3" />}
          </motion.div>
        </motion.div>
      </div>
    );
  };

  const renderFooter = () => {
    const buttonText =
      pendingRewards.length > 1
        ? t("referralRewardsAcknowledgeRewardsModal.nextRewardButton")
        : t("referralRewardsAcknowledgeRewardsModal.bankItButton");

    return (
      <Button
        fullWidth
        size="large"
        onClick={handleAcknowledge}
        disabled={acknowledgeMutation.isPending}
      >
        {acknowledgeMutation.isPending
          ? t("referralRewardsAcknowledgeRewardsModal.acknowledgingButton")
          : buttonText}
      </Button>
    );
  };

  const renderContent = () => {
    if (!currentReward)
      return (
        <div className="flex min-h-[50vh] flex-1 items-center justify-center text-center text-2xl font-bold">
          {t("referralRewardsAcknowledgeRewardsModal.noRewardsToAcknowledge")}
        </div>
      );

    return currentReward.type === "bonus" ? renderBonusContent() : renderRegularContent();
  };

  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 z-50 flex min-h-screen w-full flex-col items-center justify-center bg-black/35"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="relative flex h-full max-h-full w-full flex-col overflow-y-auto rounded-none bg-white shadow-2xl desk:h-auto desk:max-h-[calc(100%_-_4rem)] desk:max-w-lg desk:rounded-2xl">
              <MobileViewportContainer
                header={renderHeader()}
                bottomCta={renderFooter()}
                contentClassName="px-8 pb-4 pt-0"
                className="desk:max-w-none"
                showAnimatedBackground={true}
              >
                {renderContent()}
              </MobileViewportContainer>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
};

function getPersonFirstNameLastInitial(name: string) {
  const nameParts = name.split(" ");

  if (nameParts.length < 2) {
    return name;
  }

  const firstName = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1).toLowerCase();
  const lastInitial = nameParts[1].charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}.`;
}
